import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ErrorService } from '../../../shared/services/error.service';
import { ContractsService } from '../../../services/contracts.service';
import { ConfigOrder, ConfigOrdersService } from '../../../services/config-orders.service';
import {
  ServiceOrderOpeningReason,
  ServiceOrderOpeningReasonsService
} from '../../../services/service-order-opening-reasons.service';
import { AuthService } from '../../../services/auth.service';
import {
  B2bContractPlateOption,
  B2bContractPlateSelectComponent
} from '../../b2b/b2b-contract-plate-select/b2b-contract-plate-select.component';
import {
  ProviderSelection,
  ProviderSelectionModalComponent
} from '../provider-selection-modal/provider-selection-modal.component';
import {
  BudgetLineInput,
  BudgetServiceOrderCreateFlowService
} from './budget-service-order-create-flow.service';
import { BudgetServiceOrdersService } from '../../../services/budget-service-orders.service';
import {
  ServiceOrderWorkSectionItem,
  ServiceOrderWorkSectionService,
  ServiceOrderWorkSectionsService
} from '../../../services/service-order-work-sections.service';
import {
  datetimeLocalToRecordedAtIso,
  defaultDatetimeLocalValue
} from '../../../shared/utils/action-recorded-at.util';
import { ServiceType } from '../../../models/ExternalServiceProviderEnums';

const MAINTENANCE_PREVENTIVA = 'Preventiva';
const MAINTENANCE_CORRETIVA = 'Corretiva';
const GUINCHO_SERVICE_TYPE = ServiceType.GUINCHO;

/**
 * Triagem de criação de orçamento B2B em página dedicada.
 * O setor B2B informa contrato, placa, oficina e serviço; o submit cria
 * OS terceirizada + seção de trabalho + serviço + orçamento (status Pendente).
 * Pode ser pré-preenchida a partir de um agendamento (query params).
 */
@Component({
  selector: 'app-budget-service-order-create',
  templateUrl: './budget-service-order-create.component.html',
  styleUrls: ['./budget-service-order-create.component.css']
})
export class BudgetServiceOrderCreateComponent implements OnInit {
  @ViewChild(ProviderSelectionModalComponent) providerModal?: ProviderSelectionModalComponent;
  @ViewChild(B2bContractPlateSelectComponent) plateSelect?: B2bContractPlateSelectComponent;

  /** Índice da linha de serviço escolhendo fornecedor de guincho; null = oficina principal. */
  private guinchoPickerLineIndex: number | null = null;

  form!: FormGroup;
  submitting = false;
  loadingEdit = false;

  /** Preenchido na rota /:id/editar. */
  editingBudgetId: number | null = null;

  contractOptions: Array<{ id: number; label: string }> = [];
  configOrderOptions: ConfigOrder[] = [];
  openingReasonOptions: string[] = [];
  loadingContracts = false;
  loadingConfigs = false;
  loadingOpeningReasons = false;

  selectedPlateOption: B2bContractPlateOption | null = null;
  selectedProvider: ProviderSelection | null = null;

  readonly maintenanceKinds = [
    { value: MAINTENANCE_CORRETIVA, label: 'Corretiva' },
    { value: MAINTENANCE_PREVENTIVA, label: 'Preventiva' }
  ] as const;

  /** Quando veio da agenda B2B (compareceu → orçamento). */
  linkedAppointmentId: number | null = null;
  /** Agendamento em oficina parceira (exige prestador externo). */
  linkedPartnerAppointment = false;

  private appointmentPrefillPlate = '';
  private appointmentPrefillVehicleId: number | null = null;
  private lastContractId: number | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private errorService: ErrorService,
    private createFlowService: BudgetServiceOrderCreateFlowService,
    private budgetService: BudgetServiceOrdersService,
    private workSectionsService: ServiceOrderWorkSectionsService,
    private contractsService: ContractsService,
    private configOrdersService: ConfigOrdersService,
    private openingReasonsService: ServiceOrderOpeningReasonsService,
    private authService: AuthService
  ) {}

  /** Exibe o campo "% a mais" (acréscimo) nas linhas do orçamento. */
  get canViewChargePercent(): boolean {
    return true;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      b2bContractId: [null, Validators.required],
      plate: ['', Validators.required],
      configOrderId: [null, Validators.required],
      openingReason: [''],
      recordedAt: [defaultDatetimeLocalValue()],
      services: this.fb.array([this.createLineGroup()]),
      products: this.fb.array([])
    });

    this.applyAppointmentPrefill();
    this.resolveEditMode();
    this.loadContracts();
    this.loadConfigOrders();
  }

  get isEditMode(): boolean {
    return !!this.editingBudgetId;
  }

  private resolveEditMode(): void {
    const id = Number(this.route.snapshot.paramMap.get('id') || 0);
    if (id > 0) {
      this.editingBudgetId = id;
      this.loadExistingBudget(id);
    }
  }

  private applyAppointmentPrefill(): void {
    const q = this.route.snapshot.queryParamMap;
    const appointmentId = Number(q.get('appointmentId') || 0);
    const b2bContractId = Number(q.get('b2bContractId') || 0);
    const plate = String(q.get('plate') || '').trim().toUpperCase();
    const vehicleId = Number(q.get('vehicleId') || 0) || null;
    const externalProviderId = Number(q.get('externalServiceProviderId') || 0) || null;
    const externalProviderName = String(q.get('externalServiceProviderName') || '').trim();
    this.linkedPartnerAppointment = q.get('isPartnerAppointment') === 'true';

    if (appointmentId > 0) {
      this.linkedAppointmentId = appointmentId;
    }

    if (b2bContractId > 0 || plate) {
      this.lastContractId = b2bContractId > 0 ? b2bContractId : null;
      this.form.patchValue({
        b2bContractId: b2bContractId > 0 ? b2bContractId : null,
        plate: plate || ''
      });
    }

    if (plate) {
      this.appointmentPrefillPlate = plate;
      this.appointmentPrefillVehicleId = vehicleId;
      const driverName = String(q.get('driverName') || '').trim();
      this.selectedPlateOption = {
        plate,
        vehicleId,
        label: driverName ? `${plate} — ${driverName}` : plate
      };
    }

    if (externalProviderId != null && externalProviderId > 0) {
      this.selectedProvider = {
        id: externalProviderId,
        tradeName: externalProviderName || '',
        companyName: externalProviderName || '',
        cnpj: '',
        phone: '',
        contactEmail: '',
        responsibleName: '',
        serviceType: '',
        servedStates: ''
      };
    }

    // Solicitação de guincho do portal: já inclui a linha de guincho no orçamento.
    if (q.get('fromGuincho') === 'true' && !this.hasGuinchoLine()) {
      const first = this.services.at(0);
      const onlyEmptyDefault =
        this.services.length === 1
        && !!first
        && !String(first.get('description')?.value || '').trim()
        && !first.get('isGuincho')?.value;
      if (onlyEmptyDefault) {
        this.services.setControl(0, this.createLineGroup(true));
      } else {
        this.services.push(this.createLineGroup(true));
      }
    }
  }

  /** Tipo de OS selecionado exige oficina parceira? */
  get isThirdPartyOS(): boolean {
    const configOrderId = Number(this.form?.get('configOrderId')?.value || 0);
    const cfg = this.configOrderOptions.find((c) => c.id === configOrderId);
    return cfg?.isThirdParty === true;
  }

  get executionTypeLabel(): string {
    return this.isThirdPartyOS ? 'Oficina parceira (externo)' : 'Base (oficina própria)';
  }

  private loadContracts(): void {
    this.loadingContracts = true;
    this.contractsService.listB2bContracts(1, 100).subscribe({
      next: (res) => {
        this.contractOptions = (res?.data || [])
          .map((c: any) => ({
            id: Number(c.id),
            label: `${c.contractNumber || c.id}${c.contractName ? ' — ' + c.contractName : ''}`
          }))
          .filter((c: { id: number }) => c.id > 0);
        this.loadingContracts = false;
        this.syncAppointmentPlatePrefill();
      },
      error: () => {
        this.contractOptions = [];
        this.loadingContracts = false;
      }
    });
  }

  private loadConfigOrders(): void {
    this.loadingConfigs = true;
    this.configOrdersService.getAll().subscribe({
      next: (list) => {
        const actives = (list || []).filter(
          (c) => c.active !== false && (c.typeOS || '').toUpperCase().startsWith('B2B_')
        );
        this.configOrderOptions = actives;

        if (actives.length === 0) {
          this.loadingConfigs = false;
          return;
        }

        const preferThirdParty = this.linkedPartnerAppointment || !!this.selectedProvider?.id;
        const preferred = preferThirdParty
          ? actives.find((c) => c.isThirdParty === true)
          : actives.find((c) => c.isThirdParty !== true) ?? actives[0];

        if (preferred) {
          this.form.patchValue({ configOrderId: preferred.id });
          this.loadOpeningReasons(preferred.id);
        }
        this.loadingConfigs = false;
      },
      error: () => {
        this.configOrderOptions = [];
        this.loadingConfigs = false;
      }
    });
  }

  /**
   * O Order valida OpeningReason contra os motivos cadastrados para o ConfigOrder,
   * então as opções vêm do backend e o campo fica vazio quando não há cadastro.
   */
  private loadOpeningReasons(configOrderId: number): void {
    this.openingReasonOptions = [];
    this.form.patchValue({ openingReason: '' });

    if (!configOrderId) {
      return;
    }

    this.loadingOpeningReasons = true;
    this.openingReasonsService.getByConfigOrderId(configOrderId).subscribe({
      next: (reasons: ServiceOrderOpeningReason[]) => {
        this.openingReasonOptions = (reasons || [])
          .map((r) => (r?.statusName || '').trim())
          .filter((name) => !!name);

        if (this.openingReasonOptions.length === 1) {
          this.form.patchValue({ openingReason: this.openingReasonOptions[0] });
        }
        this.loadingOpeningReasons = false;
      },
      error: () => {
        this.openingReasonOptions = [];
        this.loadingOpeningReasons = false;
      }
    });
  }

  onConfigOrderChange(): void {
    this.loadOpeningReasons(Number(this.form.get('configOrderId')?.value || 0));
    if (!this.isThirdPartyOS) {
      this.clearSelectedProvider();
    }
  }

  get contractId(): number | null {
    const id = Number(this.form?.get('b2bContractId')?.value || 0);
    return id > 0 ? id : null;
  }

  get configOrderId(): number | null {
    const id = Number(this.form?.get('configOrderId')?.value || 0);
    return id > 0 ? id : null;
  }

  get services(): FormArray {
    return this.form.get('services') as FormArray;
  }

  get products(): FormArray {
    return this.form.get('products') as FormArray;
  }

  get totalPreview(): number {
    const total = [...this.services.controls, ...this.products.controls]
      .reduce((sum, control) => sum + this.lineTotal(control), 0);
    return Math.round(total * 100) / 100;
  }

  formatCurrency(value: number | undefined | null): string {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  isInvalid(control: string): boolean {
    const c = this.form?.get(control);
    return !!c && c.touched && c.invalid;
  }

  isLineInvalid(line: AbstractControl, control: string): boolean {
    const field = line.get(control);
    return !!field && field.touched && field.invalid;
  }

  lineTotal(line: AbstractControl): number {
    const kind = String(line.get('maintenanceKind')?.value || MAINTENANCE_CORRETIVA);
    if (kind === MAINTENANCE_PREVENTIVA) {
      return 0;
    }

    const quantity = Number(line.get('quantity')?.value || 0);
    const unitValue = Number(line.get('unitValue')?.value || 0);
    const percent = this.canViewChargePercent
      ? Number(line.get('chargePercent')?.value || 0)
      : 0;
    const factor = 1 + (percent > 0 ? percent / 100 : 0);
    return Math.round(quantity * unitValue * factor * 100) / 100;
  }

  isPreventiva(line: AbstractControl): boolean {
    return String(line.get('maintenanceKind')?.value || '') === MAINTENANCE_PREVENTIVA;
  }

  onMaintenanceKindChange(line: AbstractControl): void {
    this.applyLineBillingValidators(line as FormGroup);
  }

  addService(): void {
    this.services.push(this.createLineGroup(false));
  }

  addGuinchoService(): void {
    if (this.hasGuinchoLine()) {
      this.errorService.showErrorMessage('Já existe uma linha de guincho neste orçamento.');
      return;
    }
    this.services.push(this.createLineGroup(true));
  }

  hasGuinchoLine(): boolean {
    return this.services.controls.some(c => !!c.get('isGuincho')?.value);
  }

  isGuinchoLine(line: AbstractControl): boolean {
    return !!line.get('isGuincho')?.value;
  }

  removeService(index: number): void {
    if (this.services.length > 1) this.services.removeAt(index);
  }

  addProduct(): void {
    this.products.push(this.createLineGroup());
  }

  removeProduct(index: number): void {
    this.products.removeAt(index);
  }

  onContractChange(): void {
    const id = this.contractId;
    if (id === this.lastContractId) {
      return;
    }
    this.lastContractId = id;

    if (this.linkedAppointmentId && this.appointmentPrefillPlate) {
      this.selectedPlateOption = {
        plate: this.appointmentPrefillPlate,
        vehicleId: this.appointmentPrefillVehicleId,
        label: this.selectedPlateOption?.label || this.appointmentPrefillPlate
      };
      this.form.patchValue({ plate: this.appointmentPrefillPlate });
      queueMicrotask(() => this.plateSelect?.reload());
      return;
    }

    this.form.patchValue({ plate: '' });
    this.selectedPlateOption = null;
  }

  /** Garante placa do agendamento selecionada após carregar contrato/frota. */
  private syncAppointmentPlatePrefill(): void {
    if (!this.appointmentPrefillPlate) {
      return;
    }
    this.form.patchValue({ plate: this.appointmentPrefillPlate });
    if (!this.selectedPlateOption) {
      this.selectedPlateOption = {
        plate: this.appointmentPrefillPlate,
        vehicleId: this.appointmentPrefillVehicleId,
        label: this.appointmentPrefillPlate
      };
    }
    queueMicrotask(() => this.plateSelect?.reload());
  }

  onPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedPlateOption = option;
  }

  openProviderPicker(): void {
    this.guinchoPickerLineIndex = null;
    this.providerModal?.open();
  }

  openGuinchoProviderPicker(lineIndex: number): void {
    this.guinchoPickerLineIndex = lineIndex;
    this.providerModal?.open({
      serviceTypeFilter: GUINCHO_SERVICE_TYPE,
      title: 'Selecionar fornecedor de guincho'
    });
  }

  onProviderSelected(provider: ProviderSelection): void {
    if (this.guinchoPickerLineIndex != null) {
      const line = this.services.at(this.guinchoPickerLineIndex);
      const name = provider.tradeName || provider.companyName || `Prestador #${provider.id}`;
      line.patchValue({
        guinchoProviderId: provider.id,
        guinchoProviderName: name,
        description: `Guincho — ${name}`
      });
      this.guinchoPickerLineIndex = null;
      return;
    }
    this.selectedProvider = provider;
  }

  clearSelectedProvider(): void {
    this.selectedProvider = null;
  }

  clearGuinchoProvider(line: AbstractControl): void {
    line.patchValue({
      guinchoProviderId: null,
      guinchoProviderName: '',
      description: 'Guincho'
    });
  }

  get providerLabel(): string {
    if (!this.selectedProvider) return '';
    return this.selectedProvider.tradeName || this.selectedProvider.companyName || `Prestador #${this.selectedProvider.id}`;
  }

  cancel(): void {
    if (this.editingBudgetId) {
      this.router.navigate(['/b2b/budget-service-orders', this.editingBudgetId]);
      return;
    }
    if (this.linkedAppointmentId) {
      this.router.navigate(['/b2b/appointments']);
      return;
    }
    this.router.navigate(['/b2b/budget-service-orders']);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorService.showErrorMessage('Preencha os campos obrigatórios da triagem.');
      return;
    }
    if (!this.isEditMode && this.isThirdPartyOS && !this.selectedProvider?.id) {
      this.errorService.showErrorMessage('Selecione a oficina parceira (prestador externo).');
      return;
    }

    const guinchoWithoutProvider = this.services.controls.find(
      c => !!c.get('isGuincho')?.value && !(Number(c.get('guinchoProviderId')?.value) > 0)
    );
    if (guinchoWithoutProvider) {
      this.errorService.showErrorMessage('Selecione o fornecedor de guincho na linha correspondente.');
      return;
    }

    if (this.isEditMode && this.editingBudgetId) {
      this.submitEdit(this.editingBudgetId);
      return;
    }

    const departmentId = this.resolveDepartmentId();
    if (!departmentId) {
      this.errorService.showErrorMessage(
        'Não foi possível identificar o departamento. Verifique o tipo de OS ou o usuário logado.'
      );
      return;
    }

    this.submitting = true;
    const input = this.buildCreateInput(departmentId);

    // Com agendamento, o backend cria tudo e vincula na mesma transação.
    const request$ = this.linkedAppointmentId
      ? this.createFlowService.createFromAppointment(this.linkedAppointmentId, input)
      : this.createFlowService.create(input);

    request$.subscribe({
      next: () => {
        this.submitting = false;
        this.router.navigate(
          this.linkedAppointmentId ? ['/b2b/appointments'] : ['/b2b/budget-service-orders']
        );
      },
      error: () => {
        this.submitting = false;
      }
    });
  }

  private submitEdit(budgetId: number): void {
    const raw = this.form.getRawValue();
    this.submitting = true;
    this.budgetService.replaceLines(budgetId, {
      services: this.normalizeLines(raw.services),
      products: this.normalizeLines(raw.products)
    }).subscribe({
      next: () => {
        this.submitting = false;
        this.router.navigate(['/b2b/budget-service-orders', budgetId]);
      },
      error: () => {
        this.submitting = false;
      }
    });
  }

  private loadExistingBudget(budgetId: number): void {
    this.loadingEdit = true;
    this.budgetService.getById(budgetId).subscribe({
      next: budget => {
        const wsId = Number(budget.serviceOrderWorkSectionId || 0);
        if (!wsId) {
          this.loadingEdit = false;
          this.errorService.showErrorMessage('Orçamento sem seção de trabalho vinculada.');
          this.router.navigate(['/b2b/budget-service-orders', budgetId]);
          return;
        }

        const status = String(budget.status || '').toLowerCase();
        if (status.includes('cancel') || status.includes('serviço realizado') || status.includes('servico realizado')) {
          this.loadingEdit = false;
          this.errorService.showErrorMessage('Este orçamento não pode mais ser editado.');
          this.router.navigate(['/b2b/budget-service-orders', budgetId]);
          return;
        }

        forkJoin({
          workSection: this.workSectionsService.getById(wsId),
          services: this.workSectionsService.getServicesByWorkSectionId(wsId),
          items: this.workSectionsService.getItemsByWorkSectionId(wsId)
        }).subscribe({
          next: ({ workSection, services, items }) => {
            this.patchHeaderFromExisting(budget.plateVechile, workSection);
            this.replaceLineArray(this.services, (services || []).map(s => this.lineGroupFromService(s)));
            this.replaceLineArray(this.products, (items || []).map(i => this.lineGroupFromItem(i)));
            if (this.services.length === 0 && this.products.length === 0) {
              this.services.push(this.createLineGroup());
            }
            this.form.get('b2bContractId')?.disable({ emitEvent: false });
            this.form.get('plate')?.disable({ emitEvent: false });
            this.form.get('configOrderId')?.disable({ emitEvent: false });
            this.form.get('openingReason')?.disable({ emitEvent: false });
            this.form.get('recordedAt')?.disable({ emitEvent: false });
            this.loadingEdit = false;
          },
          error: () => {
            this.loadingEdit = false;
            this.router.navigate(['/b2b/budget-service-orders', budgetId]);
          }
        });
      },
      error: () => {
        this.loadingEdit = false;
        this.goBackToList();
      }
    });
  }

  private goBackToList(): void {
    this.router.navigate(['/b2b/budget-service-orders']);
  }

  private patchHeaderFromExisting(plate: string | undefined, workSection: { externalServiceProviderId?: number | null; externalServiceProvider?: any } | null): void {
    if (plate) {
      this.form.patchValue({ plate: String(plate).trim().toUpperCase() });
    }
    const providerId = Number(workSection?.externalServiceProviderId || 0);
    const p = workSection?.externalServiceProvider;
    if (providerId > 0) {
      this.selectedProvider = {
        id: providerId,
        tradeName: p?.tradeName || '',
        companyName: p?.companyName || '',
        cnpj: p?.cnpj || '',
        phone: p?.phone || '',
        contactEmail: '',
        responsibleName: '',
        serviceType: p?.serviceType || '',
        servedStates: ''
      };
    }
  }

  private replaceLineArray(array: FormArray, groups: FormGroup[]): void {
    while (array.length) array.removeAt(0);
    groups.forEach(g => array.push(g));
  }

  private lineGroupFromService(s: ServiceOrderWorkSectionService): FormGroup {
    return this.lineGroupFromValues(
      s.description,
      s.quantity,
      s.unitValue,
      s.maintenanceKind,
      s.chargePercent,
      !!s.isGuincho,
      s.externalServiceProviderId ?? null,
      s.externalServiceProviderName ?? null
    );
  }

  private lineGroupFromItem(it: ServiceOrderWorkSectionItem): FormGroup {
    return this.lineGroupFromValues(
      it.description,
      it.quantity,
      it.unitValue,
      it.maintenanceKind,
      it.chargePercent
    );
  }

  private lineGroupFromValues(
    description: string,
    quantity: number,
    unitValue: number,
    maintenanceKind?: string | null,
    chargePercent?: number | null,
    isGuincho = false,
    guinchoProviderId: number | null = null,
    guinchoProviderName: string | null = null
  ): FormGroup {
    const group = this.createLineGroup(isGuincho);
    group.patchValue({
      maintenanceKind: String(maintenanceKind || '').toLowerCase().startsWith('prevent')
        ? MAINTENANCE_PREVENTIVA
        : MAINTENANCE_CORRETIVA,
      description: description || (isGuincho ? 'Guincho' : ''),
      quantity: quantity || 1,
      unitValue: unitValue ?? null,
      chargePercent: chargePercent ?? null,
      isGuincho,
      guinchoProviderId,
      guinchoProviderName: guinchoProviderName || ''
    });
    this.applyLineBillingValidators(group);
    return group;
  }

  private resolveDepartmentId(): number {
    const configOrderId = Number(this.form.get('configOrderId')?.value || 0);
    const selectedConfig = this.configOrderOptions.find((c) => c.id === configOrderId);
    return (
      Number(selectedConfig?.departmentId || 0)
      || Number(this.authService.getDepartmentIdFromToken() || 0)
    );
  }

  private createLineGroup(isGuincho = false): FormGroup {
    const group = this.fb.group({
      maintenanceKind: [MAINTENANCE_CORRETIVA, Validators.required],
      description: [isGuincho ? 'Guincho' : '', [Validators.required, Validators.minLength(3)]],
      quantity: [1, [Validators.required, Validators.min(1)]],
      unitValue: [null as number | null, [Validators.required, Validators.min(0.01)]],
      chargePercent: [null as number | null, [Validators.min(0), Validators.max(1000)]],
      isGuincho: [isGuincho],
      guinchoProviderId: [null as number | null],
      guinchoProviderName: ['']
    });
    this.applyLineBillingValidators(group);
    return group;
  }

  /** Ajusta validators e campos conforme preventiva × corretiva. */
  private applyLineBillingValidators(group: FormGroup): void {
    const kind = String(group.get('maintenanceKind')?.value || MAINTENANCE_CORRETIVA);
    const unit = group.get('unitValue');
    const percent = group.get('chargePercent');
    if (!unit || !percent) return;

    if (kind === MAINTENANCE_PREVENTIVA) {
      unit.setValidators([Validators.min(0)]);
      unit.setValue(0, { emitEvent: false });
      unit.disable({ emitEvent: false });
      percent.setValue(null, { emitEvent: false });
      percent.disable({ emitEvent: false });
    } else {
      unit.enable({ emitEvent: false });
      percent.enable({ emitEvent: false });
      unit.setValidators([Validators.required, Validators.min(0.01)]);
      if (Number(unit.value) === 0) {
        unit.setValue(null, { emitEvent: false });
      }
      percent.setValidators([Validators.min(0), Validators.max(1000)]);
    }

    unit.updateValueAndValidity({ emitEvent: false });
    percent.updateValueAndValidity({ emitEvent: false });
  }

  private normalizeLines(lines: unknown[]): BudgetLineInput[] {
    return (lines || []).map((line: any) => {
      const kind =
        String(line.maintenanceKind || '').trim() === MAINTENANCE_PREVENTIVA
          ? MAINTENANCE_PREVENTIVA
          : MAINTENANCE_CORRETIVA;
      const chargeRaw = this.canViewChargePercent ? line.chargePercent : null;
      const chargePercent =
        kind === MAINTENANCE_PREVENTIVA
          ? null
          : chargeRaw === null || chargeRaw === undefined || chargeRaw === ''
            ? null
            : Number(chargeRaw);
      const isGuincho = !!line.isGuincho;
      const guinchoProviderId = Number(line.guinchoProviderId || line.externalServiceProviderId || 0) || null;
      const guinchoProviderName = String(line.guinchoProviderName || line.externalServiceProviderName || '').trim() || null;
      let description = String(line.description || '').trim();
      if (isGuincho && guinchoProviderName && !description.toLowerCase().includes('guincho')) {
        description = `Guincho — ${guinchoProviderName}`;
      }

      return {
        description,
        quantity: Number(line.quantity || 0),
        unitValue: kind === MAINTENANCE_PREVENTIVA ? 0 : Number(line.unitValue || 0),
        maintenanceKind: kind,
        chargePercent:
          chargePercent != null && !Number.isNaN(chargePercent) && chargePercent > 0
            ? chargePercent
            : null,
        isGuincho,
        externalServiceProviderId: isGuincho ? guinchoProviderId : null,
        externalServiceProviderName: isGuincho ? guinchoProviderName : null
      };
    });
  }

  private buildCreateInput(departmentId: number) {
    const raw = this.form.getRawValue();
    const isBase = !this.isThirdPartyOS;
    const provider = this.selectedProvider;
    const openingReason = String(raw.openingReason || '').trim() || null;
    const vehicleFromQuery = Number(this.route.snapshot.queryParamMap.get('vehicleId') || 0) || null;

    return {
      b2bContractId: Number(raw.b2bContractId),
      plate: String(raw.plate || this.selectedPlateOption?.plate || '').trim().toUpperCase(),
      vehicleId: Number(this.selectedPlateOption?.vehicleId || vehicleFromQuery || 0) || null,
      configOrderId: Number(raw.configOrderId),
      departmentId,
      openingReason,
      providerId: isBase ? null : (provider?.id ?? null),
      providerName: isBase ? null : this.providerLabel || null,
      services: this.normalizeLines(raw.services),
      products: this.normalizeLines(raw.products),
      isScheduled: !!this.linkedAppointmentId,
      isBaseExecution: isBase,
      recordedAt: datetimeLocalToRecordedAtIso(raw.recordedAt) ?? null
    };
  }
}
